import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import {
  SendByteWallet,
  SendByteWalletAddress,
  SendByteWalletAddressCreationType,
  SendByteWalletAddressStatus,
} from "@lib/types/SendByte";
import { Form, Modal, Space } from "antd";
import { useCallback, useEffect, useState } from "react";
import useIntl from "react-intl/lib/src/components/useIntl";
import { useParams } from "react-router-dom";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { FormattedMessage } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  WalletAddressCreationTypeInput,
  WalletAddressCustomIdInput,
  WalletAddressInput,
  WalletAddressNetworkInput,
  WalletAddressProtocolInput,
  WalletAddressStatusInput,
  WalletIdInput,
} from "../WalletAddressFormInputs";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import {
  apiAddMultipleSendByteWalletAddress,
  apiAddSendByteWalletAddress,
  apiUpdateSendByteWalletAddress,
} from "@lib/api/walletSendByte";

const layout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 17 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  sendByteWalletsQuery?: UseQueryProps<PaginationType<SendByteWallet>>;
  sendByteWalletAddress?: Partial<SendByteWalletAddress>;
  customerId?: string;
};

export default function SendByteWalletAddressModal(props: Props) {
  const {
    openModal,
    toggleModal,
    sendByteWalletsQuery,
    sendByteWalletAddress,
    customerId,
  } = props;

  const { messages } = useIntl();
  const [form] = Form.useForm();
  const walletIdValue = Form.useWatch("walletId", form);
  const address = Form.useWatch("address", form);

  const walletsApiData = sendByteWalletsQuery?.data?.data?.data;

  const initialValues: Record<string, any> = {
    walletId: "",
    status: SendByteWalletAddressStatus.Available,
    creationType: SendByteWalletAddressCreationType.SINGLE,
    network: null,
    protocol: null,
    customId: null,
    address: "",
    customerId,
  } as const;

  const { walletId } = useParams();
  const [creationType, setCreationType] =
    useState<SendByteWalletAddressCreationType>(
      SendByteWalletAddressCreationType.SINGLE,
    );

  useEffect(() => {
    let obj: Record<string, any> = {
      ...initialValues,
    };

    if (openModal) {
      obj = {
        ...sendByteWalletAddress,
      };

      if (sendByteWalletAddress) {
        obj = {
          ...obj,
          walletId: sendByteWalletAddress.walletId,
        };
      } else if (walletId) {
        obj = {
          ...obj,
          walletId,
        };
      }
      form?.setFieldsValue(obj);
    }
  }, [openModal, walletId, sendByteWalletAddress]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
    setCreationType(SendByteWalletAddressCreationType.SINGLE);
  }, []);

  const handleClose = useCallback(() => {
    toggleModal(false);
    handleResetForm();
  }, [toggleModal]);

  const queryClient = useQueryClient();
  const addMultipleQuery = useMutation({
    mutationFn: apiAddMultipleSendByteWalletAddress,
    mutationKey: ["sendbyte-wallet-address"],
    onSuccess() {
      handleResetForm();
      toggleModal(false);
      toast.success(
        <FormattedMessage
          id="common.createdMessage"
          values={{ name: messages["common.walletAddresses"] as string }}
        />,
      );
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("sendbyte-wallet");
        },
      });
    },
  });
  const addQuery = useMutation({
    mutationFn: apiAddSendByteWalletAddress,
    mutationKey: ["sendbyte-wallet-address"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.createdMessage"
          values={{ name: messages["common.walletAddress"] as string }}
        />,
      );
      form.setFieldValue("address", "");
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("sendbyte-wallet");
        },
      });
      handleClose();
    },
  });
  const updateQuery = useMutation({
    mutationFn: apiUpdateSendByteWalletAddress,
    mutationKey: ["sendbyte-wallet-address"],
    onSuccess() {
      handleResetForm();
      toggleModal(false);
      toast.success(
        <FormattedMessage
          id="common.updatedMessage"
          values={{ name: messages["common.walletAddress"] as string }}
        />,
      );
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("sendbyte-wallet");
        },
      });
    },
  });

  const mutationQuery = sendByteWalletAddress ? updateQuery : addQuery;

  return (
    <Modal
      title={
        <FormattedMessage
          id={sendByteWalletAddress ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.walletAddress"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      width={{
        xs: "90%",
        sm: "80%",
        md: "70%",
        lg: "60%",
        xl: "50%",
        xxl: "40%",
      }}
    >
      <Form<SendByteWalletAddress>
        {...layout}
        form={form}
        name="add-wallet"
        autoComplete="off"
        style={{ maxWidth: 800, marginTop: 20 }}
        initialValues={initialValues}
        onFinish={(values) => {
          values.customerId = customerId;

          if (walletIdValue && walletsApiData) {
            values.customerId = walletsApiData?.find(
              (wallet) => wallet.id === walletIdValue,
            )?.customerId;
          }

          if (
            !sendByteWalletAddress &&
            creationType === SendByteWalletAddressCreationType.MULTIPLE
          ) {
            let customId = Number(values.customId);
            const addresses = address
              .split("\n")
              .filter((x) => !!x)
              .map((address) => ({
                ...values,
                address,
                customId: customId++,
              }));
            addMultipleQuery.mutate(addresses);
          } else {
            mutationQuery.mutate({ ...values, id: sendByteWalletAddress?.id });
          }
        }}
      >
        <WalletIdInput
          walletsQuery={sendByteWalletsQuery}
          form={form}
          loading={sendByteWalletsQuery?.isLoading || mutationQuery.isPending}
        />

        <WalletAddressStatusInput
          form={form}
          loading={sendByteWalletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={sendByteWalletAddress?.id}
        />

        <WalletAddressNetworkInput
          form={form}
          loading={sendByteWalletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={sendByteWalletAddress?.id}
        />

        <WalletAddressProtocolInput
          form={form}
          loading={sendByteWalletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={sendByteWalletAddress?.id}
        />

        <WalletAddressCreationTypeInput
          creationType={creationType}
          setCreationType={setCreationType as any}
          show={!sendByteWalletAddress}
          form={form}
          loading={sendByteWalletsQuery?.isLoading || mutationQuery.isPending}
        />

        <WalletAddressCustomIdInput
          creationType={creationType}
          form={form}
          loading={sendByteWalletsQuery?.isLoading || mutationQuery.isPending}
          walletsQuery={sendByteWalletsQuery}
        />

        <WalletAddressInput
          creationType={creationType}
          form={form}
          loading={sendByteWalletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={sendByteWalletAddress?.id}
        />

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            {!sendByteWalletAddress && (
              <ActionButton
                type="reset"
                onClick={handleResetForm}
                loading={
                  sendByteWalletsQuery?.isLoading || mutationQuery.isPending
                }
              />
            )}
            <ActionButton
              type={!!sendByteWalletAddress ? "save" : "add"}
              loading={
                sendByteWalletsQuery?.isLoading || mutationQuery.isPending
              }
            />
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
